import { useState, type KeyboardEvent } from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

import {
  ScreeningQuestionWithAnswer,
  ScreeningQuestionWithoutAnswer,
} from "./ScreeningAnswers";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { dates } from "@/lib/dates";
import { PastApplicationAnswers } from "@/types";

// Screening Q&A the candidate answered on their other candidacies. Read-only:
// past answers are historical records, never edited from this sheet.
export const PastApplicationAnswersDropdown = ({
  pastApplications,
}: {
  pastApplications: PastApplicationAnswers[];
}) => {
  /* React Hooks */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedApplicationIndex, setSelectedApplicationIndex] =
    useState<number>();

  /* Helpers */
  const selectedApplication =
    selectedApplicationIndex === undefined
      ? undefined
      : pastApplications[selectedApplicationIndex];

  /* Handlers */
  const handleSelectApplication = (index: number) => {
    setIsDropdownOpen(false);
    setSelectedApplicationIndex(index);
  };

  const handlePreviousApplication = () => {
    setSelectedApplicationIndex((current) => {
      if (current === undefined || current === 0) return current;
      return current - 1;
    });
  };

  const handleNextApplication = () => {
    setSelectedApplicationIndex((current) => {
      if (
        current === undefined ||
        current === pastApplications.length - 1
      ) {
        return current;
      }
      return current + 1;
    });
  };

  const handleDialogKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }
    // stopPropagation keeps the arrows from reaching SheetNavigation's
    // document-level listener, which would move to another candidacy.
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      event.stopPropagation();
      handlePreviousApplication();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      event.stopPropagation();
      handleNextApplication();
    }
  };

  /* Render */
  const renderApplicationRow = (
    application: PastApplicationAnswers,
    index: number,
  ) => {
    const appliedAtLabel = dates.formatIsoDateForUser(application.createdAt);

    return (
      <Row
        className="min-w-0 cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-slate-50"
        key={application.candidacyId}
        onClick={() => {
          handleSelectApplication(index);
        }}
      >
        <Column className="min-w-0 flex-1 gap-1.5">
          <Text className="truncate text-sm font-medium text-slate-800">
            {application.jobTitle}
          </Text>
          <Text className="truncate text-xs font-normal text-slate-400">
            {application.companyName} • {appliedAtLabel ?? "—"}
          </Text>
        </Column>
      </Row>
    );
  };

  const renderScreeningAnswer = (
    screeningAnswer: PastApplicationAnswers["screeningAnswers"][number],
  ) => {
    const answer = (screeningAnswer.answer ?? "").trim();
    if (!answer) {
      return (
        <ScreeningQuestionWithoutAnswer
          key={screeningAnswer.id}
          question={screeningAnswer.question}
        />
      );
    }
    return (
      <ScreeningQuestionWithAnswer
        answer={answer}
        key={screeningAnswer.id}
        question={screeningAnswer.question}
      />
    );
  };

  const renderDialog = () => {
    if (!selectedApplication || selectedApplicationIndex === undefined) {
      return null;
    }

    const isFirstApplication = selectedApplicationIndex === 0;
    const isLastApplication =
      selectedApplicationIndex === pastApplications.length - 1;
    const appliedAtLabel = dates.formatIsoDateForUser(
      selectedApplication.createdAt,
    );

    return (
      <Dialog
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedApplicationIndex(undefined);
        }}
        open
      >
        <DialogContent
          className="flex h-[720px] max-h-[calc(100vh-2rem)] max-w-xl flex-col gap-0 overflow-hidden p-0"
          onKeyDown={handleDialogKeyDown}
        >
          <DialogHeader className="border-b border-slate-200 px-6 py-5 text-left">
            <DialogTitle className="text-lg font-semibold text-slate-900">
              {selectedApplication.jobTitle}
            </DialogTitle>
            <Text className="text-sm font-normal text-slate-500">
              {selectedApplication.companyName} • {appliedAtLabel ?? "—"}
            </Text>
          </DialogHeader>
          <Column className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {selectedApplication.screeningAnswers.map(renderScreeningAnswer)}
          </Column>
          <Row className="items-center justify-between border-t border-slate-200 px-6 py-4">
            <Text className="text-xs font-normal text-slate-400">
              {selectedApplicationIndex + 1} of{" "}
              {pastApplications.length}
            </Text>
            <Row className="items-center gap-2">
              <Button
                disabled={isFirstApplication}
                onClick={handlePreviousApplication}
                size="icon"
                title="Previous application"
                type="button"
                variant="outline"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                disabled={isLastApplication}
                onClick={handleNextApplication}
                size="icon"
                title="Next application"
                type="button"
                variant="outline"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </Row>
          </Row>
        </DialogContent>
      </Dialog>
    );
  };

  const render = () => {
    return (
      <>
        <DropdownMenu onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-auto gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
              type="button"
              variant="ghost"
            >
              {pastApplications.length} Past Apps
              <ChevronDownIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="max-h-[60vh] w-96 space-y-1 overflow-y-auto overscroll-contain p-2"
          >
            {pastApplications.map(renderApplicationRow)}
          </DropdownMenuContent>
        </DropdownMenu>
        {renderDialog()}
      </>
    );
  };

  return render();
};